/**
 * In-flight device probes, by the `correlationId` we sent them with.
 *
 * GAIA answers a probe as `reactive-solve`, indistinguishable from a question the expert typed, so
 * `useGaiaMessageEvents` would render it. The echoed `correlationId` is the only marker we get.
 */
const pending = new Set<string>();

/**
 * Bounds the set when a reply never arrives. Deliberately the only cleanup: clearing on a timeout
 * would let a late reply through to the timeline as a message the expert never asked for.
 */
const MAX_PENDING = 50;

export const registerDeviceProbe = (correlationId: string) => {
    if (pending.size >= MAX_PENDING) {
        // Set preserves insertion order, so this is the oldest.
        const oldest = pending.values().next().value;
        if (oldest) pending.delete(oldest);
    }
    pending.add(correlationId);
};

export const isDeviceProbe = (correlationId?: string) => !!correlationId && pending.has(correlationId);

export const clearDeviceProbe = (correlationId?: string) => {
    if (correlationId) pending.delete(correlationId);
};

/** Sessions already probed. Each probe is a full reactive invoke, so it fires at most once per session. */
const probedSessions = new Set<string>();

export const hasProbedSession = (sessionId: string) => probedSessions.has(sessionId);

export const markSessionProbed = (sessionId: string) => {
    probedSessions.add(sessionId);
};

/** Called on session end so a reused session id can be probed again. */
export const clearProbedSession = (sessionId: string) => {
    probedSessions.delete(sessionId);
};
